import React, { FC, useState } from 'react';
import { Avatar, Typography, IconButton, Tooltip } from '@getgo/chameleon-web/react';
import classNames from 'classnames';
import { ReactComponent as Facebook } from './../../../svg/ic_facebook.svg';
import { ReactComponent as Linkedin } from './../../../svg/ic_linkedin.svg';
import { ReactComponent as Twitter } from './../../../svg/ic_twitter.svg';
import { GlobeIcon } from '@getgo/chameleon-icons/react';
import { IEventDetail } from '../eventInterface';
import { SpeakerUtils } from '../../utils/speakerUtils';
import FormatStringUrl from '../../utils/formatStringUrl';
import { useTranslation } from 'react-i18next';
import { onClickOfSpeakerSocialMediaLinkTracker } from '../../../mixpanel/Feature/eventTracker';

export interface IEventSpeakers {
  eventDetail: IEventDetail;
  activeSpeakerId: string;
  speakerListRef: { [index: string]: any };
  eventKey?: string;
}

const EventSpeakers: FC<IEventSpeakers> = ({
  eventDetail,
  activeSpeakerId,
  speakerListRef,
  eventKey,
}) => {
  const { t } = useTranslation();
  const toolTipIsVisble = {
    website: false,
    twitter: false,
    linkedin: false,
    facebook: false,
    gotostage: false,
  };
  const eventSpeakerToolTipVisible: { [key: string]: any } = {};
  eventDetail?.speakers?.forEach((speaker) => {
    eventSpeakerToolTipVisible[speaker.id] = { ...toolTipIsVisble };
  });
  const [isVisible, setIsVisible] = useState({ ...eventSpeakerToolTipVisible });
  const socialMedialHandler = (socialMediaName: string, link: string) => {
    onClickOfSpeakerSocialMediaLinkTracker({ socialMediaName, link, eventKey });
  };
  return (
    <div className="event-speaker-box">
      {eventDetail?.speakers?.map((speaker: any) => (
        <div
          key={speaker.id}
          className={classNames('event-speaker', {
            selected: speaker.id === activeSpeakerId,
          })}
          ref={(element) => (speakerListRef.current[speaker.id] = element)}
        >
          <div className="event-speaker-avatar">
            {speaker?.imgsrc ? (
              <Avatar size="xxlarge">
                <img alt={speaker.name} src={speaker.imgsrc} />
              </Avatar>
            ) : (
              <Avatar size="xxlarge">{SpeakerUtils.getInitials(speaker.name)}</Avatar>
            )}
          </div>
          <div>
            <div id={`event-speaker-name-${speaker.id}`} className="line-break event-speaker-name">
              <Typography color="text-01" variant="heading-small">
                {speaker.name ? speaker.name : ''}
              </Typography>
            </div>
            <div
              id={`event-speaker-job-details-${speaker.id}`}
              className="line-break event-speaker-job-details"
            >
              <Typography color="text-02">
                {`${speaker.jobTitle ? speaker.jobTitle : ''}${
                  speaker.jobTitle && speaker.organization ? ',' : ''
                }${speaker.organization ? speaker.organization : ''}`}
              </Typography>
            </div>
            <div className="event-speaker-description">
              <Typography color="text-01">
                <FormatStringUrl>{speaker.bio}</FormatStringUrl>
              </Typography>
            </div>
            <div className="event-hosted-by-details-icon">
              {speaker.urls?.website ? (
                <a
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    socialMedialHandler(
                      'website',
                      speaker.urls?.website.indexOf('http://') > -1 ||
                        speaker.urls?.website.indexOf('https://') > -1
                        ? speaker.urls?.website
                        : `http://${speaker.urls?.website}`,
                    )
                  }
                  href={
                    speaker.urls?.website.indexOf('http://') > -1 ||
                    speaker.urls?.website.indexOf('https://') > -1
                      ? speaker.urls?.website
                      : `http://${speaker.urls?.website}`
                  }
                  className="speaker-section-link"
                >
                  <div className="icon-box">
                    <Tooltip
                      position={'bottom' as any}
                      label={t('event.addedSocialMedia.website')}
                      visible={isVisible[speaker.id].website}
                    >
                      <IconButton
                        variant="secondary"
                        size="xsmall"
                        onMouseEnter={(e) =>
                          setIsVisible({
                            ...eventSpeakerToolTipVisible,
                            [speaker.id]: { ...toolTipIsVisble, website: true },
                          })
                        }
                        onMouseLeave={(e) =>
                          setIsVisible({
                            ...eventSpeakerToolTipVisible,
                            [speaker.id]: { ...toolTipIsVisble, website: false },
                          })
                        }
                      >
                        <GlobeIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                </a>
              ) : null}
              {speaker.urls?.twitter ? (
                <a
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    socialMedialHandler(
                      'twitter',
                      speaker.urls?.twitter.indexOf('http://') > -1 ||
                        speaker.urls?.twitter.indexOf('https://') > -1
                        ? speaker.urls?.twitter
                        : `http://${speaker.urls?.twitter}`,
                    )
                  }
                  href={
                    speaker.urls?.twitter.indexOf('http://') > -1 ||
                    speaker.urls?.twitter.indexOf('https://') > -1
                      ? speaker.urls?.twitter
                      : `http://${speaker.urls?.twitter}`
                  }
                  className="speaker-section-link"
                >
                  <div className="icon-box">
                    <Tooltip
                      position={'bottom' as any}
                      label={t('event.addedSocialMedia.twitter')}
                      visible={isVisible[speaker.id].twitter}
                    >
                      <IconButton
                        variant="secondary"
                        size="xsmall"
                        onMouseEnter={(e) =>
                          setIsVisible({
                            ...eventSpeakerToolTipVisible,
                            [speaker.id]: { ...toolTipIsVisble, twitter: true },
                          })
                        }
                        onMouseLeave={(e) =>
                          setIsVisible({
                            ...eventSpeakerToolTipVisible,
                            [speaker.id]: { ...toolTipIsVisble, twitter: false },
                          })
                        }
                      >
                        <Twitter />
                      </IconButton>
                    </Tooltip>
                  </div>
                </a>
              ) : null}
              {speaker.urls?.facebook ? (
                <a
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    socialMedialHandler(
                      'facebook',
                      speaker.urls?.facebook.indexOf('http://') > -1 ||
                        speaker.urls?.facebook.indexOf('https://') > -1
                        ? speaker.urls?.facebook
                        : `http://${speaker.urls?.facebook}`,
                    )
                  }
                  href={
                    speaker.urls?.facebook.indexOf('http://') > -1 ||
                    speaker.urls?.facebook.indexOf('https://') > -1
                      ? speaker.urls?.facebook
                      : `http://${speaker.urls?.facebook}`
                  }
                  className="speaker-section-link"
                >
                  <div className="icon-box">
                    <Tooltip
                      position={'bottom' as any}
                      label={t('event.addedSocialMedia.facebook')}
                      visible={isVisible[speaker.id].facebook}
                    >
                      <IconButton
                        variant="secondary"
                        size="xsmall"
                        onMouseEnter={(e) =>
                          setIsVisible({
                            ...eventSpeakerToolTipVisible,
                            [speaker.id]: { ...toolTipIsVisble, facebook: true },
                          })
                        }
                        onMouseLeave={(e) =>
                          setIsVisible({
                            ...eventSpeakerToolTipVisible,
                            [speaker.id]: { ...toolTipIsVisble, facebook: false },
                          })
                        }
                      >
                        <Facebook />
                      </IconButton>
                    </Tooltip>
                  </div>
                </a>
              ) : null}
              {speaker.urls?.linkedin ? (
                <a
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    socialMedialHandler(
                      'linkedin',
                      speaker.urls?.linkedin.indexOf('http://') > -1 ||
                        speaker.urls?.linkedin.indexOf('https://') > -1
                        ? speaker.urls?.linkedin
                        : `http://${speaker.urls?.linkedin}`,
                    )
                  }
                  href={
                    speaker.urls?.linkedin.indexOf('http://') > -1 ||
                    speaker.urls?.linkedin.indexOf('https://') > -1
                      ? speaker.urls?.linkedin
                      : `http://${speaker.urls?.linkedin}`
                  }
                  className="speaker-section-link"
                >
                  <div className="icon-box">
                    <Tooltip
                      position={'bottom' as any}
                      label={t('event.addedSocialMedia.linkedin')}
                      visible={isVisible[speaker.id].linkedin}
                    >
                      <IconButton
                        variant="secondary"
                        size="xsmall"
                        onMouseEnter={(e) =>
                          setIsVisible({
                            ...eventSpeakerToolTipVisible,
                            [speaker.id]: { ...toolTipIsVisble, linkedin: true },
                          })
                        }
                        onMouseLeave={(e) =>
                          setIsVisible({
                            ...eventSpeakerToolTipVisible,
                            [speaker.id]: { ...toolTipIsVisble, linkedin: false },
                          })
                        }
                      >
                        <Linkedin />
                      </IconButton>
                    </Tooltip>
                  </div>
                </a>
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default EventSpeakers;
